import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { InfoCircle, Refresh } from 'iconsax-reactjs';
import {
  KNOT_TIMEFRAMES,
  defaultKnotTolerance,
  type Candle,
  type Knot,
  type KnotStats,
  type Timeframe,
} from '@pipyar/core';
import { api } from '@/lib/api';
import { useTheme } from '@/lib/useTheme';
import { useSymbol } from '@/lib/useSymbol';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import { KnotChart } from '@/components/chart/KnotChart';
import { ChartFrame } from '@/components/chart/ChartFrame';
import { TimeframeButtons } from '@/components/chart/TimeframeButtons';
import { chartPalette } from '@/components/chart/chartTheme';
import type { KnotLine } from '@/components/chart/knotPrimitive';
import { useI18n } from '@/lib/i18n';

interface KnotsResponse {
  symbol: string;
  timeframe: Timeframe;
  digits: number;
  storedGmtOffsetSec: number;
  tolerance: number;
  maxAge: number;
  lastClose: number;
  candles: Candle[];
  knots: Knot[];
  stats: KnotStats;
  nearest: { above: Knot[]; below: Knot[] };
}

/**
 * Tolerance presets, as a share of the forming candle's body.
 *
 * A fraction rather than a price so one setting means the same thing on every
 * timeframe — a fixed distance would be unreachable on M5 and trivial on D1.
 */
const TOLERANCES = [
  // 0% is an exact tick match. On a 2-digit instrument roughly half of all
  // knots sit on a half-cent that no close can ever equal, so at this setting
  // the module effectively just draws the levels.
  { value: 0, label: '0%' },
  { value: 0.01, label: '1%' },
  { value: 0.02, label: '2%' },
  { value: 0.03, label: '3%' },
  { value: 0.04, label: '4%' },
  { value: 0.05, label: '5%' },
  { value: 0.1, label: '10%' },
  { value: 0.2, label: '20%' },
];

export function KnotsPage() {
  const { theme } = useTheme();
  const { symbol } = useSymbol();
  const { t } = useI18n();
  const [timeframe, setTimeframeState] = useState<Timeframe>('M15');
  const [tolerance, setTolerance] = useState(() => defaultKnotTolerance('M15'));
  // Each timeframe opens at its own default tolerance (10% on W1, 3% elsewhere);
  // a preset picked by hand holds until the timeframe changes.
  const setTimeframe = (tf: Timeframe) => {
    setTimeframeState(tf);
    setTolerance(defaultKnotTolerance(tf));
  };
  const [showBands, setShowBands] = useState(true);

  const query = useQuery({
    queryKey: ['knots', symbol, timeframe, tolerance],
    queryFn: () =>
      api.get<KnotsResponse>(
        `/api/market/knots?symbol=${encodeURIComponent(symbol!)}&timeframe=${timeframe}` +
          `&tolerance=${tolerance}`,
      ),
    enabled: Boolean(symbol),
    staleTime: 20_000,
    refetchInterval: 30_000,
  });

  const palette = chartPalette(theme);
  const digits = query.data?.digits ?? 2;
  const stats = query.data?.stats;

  const lines = useMemo<KnotLine[]>(
    () =>
      (query.data?.knots ?? []).map((k) => ({
        time: k.time,
        level: k.level,
        tolerance: k.tolerance,
        direction: k.direction,
        hitTime: k.hit?.time ?? null,
        pendingTime: k.pending?.time ?? null,
      })),
    [query.data],
  );

  const error = query.error;

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('knots.title')}</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {t('knots.subtitle', { symbol: symbol ?? '—' })}
          </p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          aria-label={t('common.refresh')}
          className="ms-auto"
          onClick={() => void query.refetch()}
        >
          <Refresh size={18} className={query.isFetching ? 'animate-spin' : undefined} />
        </Button>
      </header>

      {error && (
        <div
          role="alert"
          className="flex gap-2.5 rounded-lg border border-danger/30 bg-danger/8 p-3 text-sm text-danger"
        >
          <InfoCircle size={18} variant="Bold" className="mt-px shrink-0" />
          <span>{error instanceof Error ? error.message : t('knots.error')}</span>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-[1fr_19rem]">
        {/* min-w-0 is load-bearing: a 1fr grid column defaults to
            min-width:auto, so it cannot shrink below its content. Once the
            chart canvas has been sized to the viewport by full screen, the
            column would stay that wide on exit and the page would scroll
            sideways forever. */}
        <section className="order-2 min-w-0 space-y-3 lg:order-1">
          <div className="flex flex-wrap items-center gap-1">
            <TimeframeButtons value={timeframe} onChange={setTimeframe} options={KNOT_TIMEFRAMES} />

            <span className="ms-auto flex items-center gap-1">
              <ToleranceButtons value={tolerance} onChange={setTolerance} />
            </span>
          </div>

          <ChartFrame
            label={t('knots.chart')}
            fullscreenToolbar={
              <>
                <TimeframeButtons
                  value={timeframe}
                  onChange={setTimeframe}
                  options={KNOT_TIMEFRAMES}
                  compact
                />
                <span className="mx-1 h-4 w-px bg-border" aria-hidden="true" />
                <ToleranceButtons value={tolerance} onChange={setTolerance} />
              </>
            }
            className="group/frame h-[32rem] overflow-hidden glass rounded-2xl"
          >
            {query.isPending ? (
              <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                {t('common.loading')}
              </div>
            ) : (query.data?.candles.length ?? 0) === 0 ? (
              <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                No candles for {timeframe} yet.
              </div>
            ) : (
              <KnotChart
                className="h-full w-full"
                candles={query.data!.candles}
                knots={lines}
                showBands={showBands}
                digits={digits}
                theme={theme}
                resetKey={`${symbol}-${timeframe}`}
              />
            )}
          </ChartFrame>

          <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
            <Toggle checked={showBands} onChange={setShowBands} label={t('knots.showBand')} />
            <span className="ms-auto">
              {t('knots.legend')}{' '}
              <Swatch color={palette.knotLatest} /> {t('knots.latest')}{' '}
              <Swatch color={palette.knotPending} dashed /> {t('knots.pending')}
            </span>
          </div>
        </section>

        <aside className="order-1 space-y-3 lg:order-2">
          <div className="glass rounded-2xl p-3">
            <h2 className="mb-2.5 text-sm font-medium">{t('knots.thisWindow')}</h2>
            {stats ? (
              <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
                <Stat label={t('knots.formed')} value={stats.total.toLocaleString()} />
                <Stat
                  label={t('knots.taken')}
                  value={
                    stats.hitRate === null ? '—' : `${Math.round(stats.hitRate * 100)}%`
                  }
                />
                <Stat label={t('knots.stillLive')} value={stats.live.toLocaleString()} />
                <Stat
                  label={t('knots.medianWait')}
                  value={stats.medianBarsToHit === null ? '—' : `${stats.medianBarsToHit} bars`}
                />
                <Stat label={t('knots.takenFromAbove')} value={stats.fromAbove.toLocaleString()} />
                <Stat label={t('knots.fromBelow')} value={stats.fromBelow.toLocaleString()} />
              </dl>
            ) : (
              <p className="text-xs text-muted-foreground">{t('common.loading')}</p>
            )}
            <p className="mt-2.5 text-[10px] leading-snug text-muted-foreground">
              {t('knots.rateNote')}
            </p>
          </div>

          <NearestList
            title={t('knots.liveAbove')}
            knots={query.data?.nearest.above ?? []}
            digits={digits}
            reference={query.data?.lastClose}
          />
          <NearestList
            title={t('knots.liveBelow')}
            knots={query.data?.nearest.below ?? []}
            digits={digits}
            reference={query.data?.lastClose}
          />
        </aside>
      </div>
    </div>
  );
}

/**
 * The tolerance presets.
 *
 * Extracted so the page toolbar and the full-screen overlay render the SAME
 * control against the same state, rather than two copies that could drift.
 */
function ToleranceButtons({
  value,
  onChange,
}: {
  value: number;
  onChange: (v: number) => void;
}) {
  const { t } = useI18n();

  return (
    <>
      <span className="me-1 text-xs text-muted-foreground">{t('common.tolerance')}</span>
      {TOLERANCES.map((t) => (
        <button
          key={t.value}
          type="button"
          onClick={() => onChange(t.value)}
          aria-current={value === t.value ? 'true' : undefined}
          className={cn(
            'rounded-lg px-2.5 py-1.5 text-xs font-medium transition-colors',
            value === t.value
              ? 'bg-primary text-primary-foreground'
              : 'bg-elevated text-muted-foreground hover:text-foreground',
          )}
        >
          {t.label}
        </button>
      ))}
    </>
  );
}

function NearestList({
  title,
  knots,
  digits,
  reference,
}: {
  title: string;
  knots: Knot[];
  digits: number;
  reference: number | undefined;
}) {
  const { t } = useI18n();

  return (
    <div className="glass rounded-2xl p-3">
      <h2 className="mb-2 text-sm font-medium">{title}</h2>
      {knots.length === 0 ? (
        <p className="text-xs text-muted-foreground">{t('knots.noneInRange')}</p>
      ) : (
        <ul className="space-y-1">
          {knots.map((k) => (
            <li key={k.time} className="flex items-center gap-2 text-xs">
              <span
                className={cn(
                  'size-2 shrink-0 rounded-sm',
                  k.direction === 'up' ? 'bg-bull' : k.direction === 'down' ? 'bg-bear' : 'bg-muted',
                )}
                aria-hidden="true"
              />
              <span className="tabular">{k.level.toFixed(digits)}</span>
              {reference !== undefined && (
                <span className="tabular ms-auto text-[11px] text-muted-foreground">
                  {k.level >= reference ? '+' : ''}
                  {(k.level - reference).toFixed(digits)}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Toggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-1.5 select-none">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="size-3.5 rounded border-border accent-[var(--primary)]"
      />
      {label}
    </label>
  );
}

function Swatch({ color, dashed = false }: { color: string; dashed?: boolean }) {
  return (
    <span
      className="me-0.5 inline-block h-0.5 w-3 align-middle"
      style={
        dashed
          ? { backgroundImage: `linear-gradient(to right, ${color} 60%, transparent 60%)`, backgroundSize: '5px 2px' }
          : { backgroundColor: color }
      }
      aria-hidden="true"
    />
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-[10px] text-muted-foreground">{label}</dt>
      <dd className="tabular font-medium">{value}</dd>
    </div>
  );
}
